"use client";

import type {
	ArtistSearchResult,
	FestivalSearchResult,
	SearchDialogProps,
	ShowSearchResult,
	UserSearchResult,
} from "@workspace/types";
import { cn } from "@workspace/ui/lib/utils";
import { useCallback, useEffect, useRef } from "react";
import { useSearchKeyboardNav } from "@/hooks/features/search";
import { SearchResults } from "../results/search-results";
import { SearchModalInput } from "./search-input";

export interface ExtendedSearchDialogProps extends SearchDialogProps {
	searchValue: string;
	onSearchValueChange: (value: string) => void;
	selectedIndex: number;
	onSelectedIndexChange: (index: number) => void;
	userResults: UserSearchResult[];
	festivalResults: FestivalSearchResult[];
	showResults: ShowSearchResult[];
	artistResults: ArtistSearchResult[];
	isLoading: boolean;
	error: string | null;
	onSelectUser: (username: string) => void;
	onSelectFestival: (festivalSlug: string) => void;
	onSelectShow: (show: ShowSearchResult) => void;
	onSelectArtist: (artist: ArtistSearchResult) => void;
}

export function SearchDialog({
	isOpen,
	onClose,
	searchValue,
	onSearchValueChange,
	selectedIndex,
	onSelectedIndexChange,
	userResults,
	festivalResults,
	showResults,
	artistResults,
	isLoading,
	error,
	onSelectUser,
	onSelectFestival,
	onSelectShow,
	onSelectArtist,
}: ExtendedSearchDialogProps) {
	const searchDialogRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const isMobile = useRef(false);

	// Calculate total results
	const totalResults =
		userResults.length +
		festivalResults.length +
		showResults.length +
		artistResults.length;

	// Check if we're on mobile
	useEffect(() => {
		const checkMobile = () => {
			isMobile.current = window.innerWidth < 768;
		};

		checkMobile();

		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	// Focus input when dialog opens
	useEffect(() => {
		if (isOpen && searchInputRef.current) {
			// Delay focus slightly on mobile to ensure animation completes first
			const delay = isMobile.current ? 300 : 50;

			const timeoutId = setTimeout(() => {
				if (searchInputRef.current) {
					searchInputRef.current.focus();

					// For mobile Safari, also try setting the selection to ensure focus
					if (isMobile.current) {
						try {
							searchInputRef.current.setSelectionRange(0, 0);
						} catch {
							// Ignore any selection errors
						}
					}
				}
			}, delay);

			return () => clearTimeout(timeoutId);
		}
	}, [isOpen]);

	// Handle result selection based on index
	const handleSelectByIndex = useCallback(
		(index: number) => {
			// Check result type based on ranking: Festival → Show → Artist → Users
			if (index < festivalResults.length) {
				const festival = festivalResults[index];
				if (festival) {
					onSelectFestival(festival.slug);
				}
			} else if (index < festivalResults.length + showResults.length) {
				const showIndex = index - festivalResults.length;
				const show = showResults[showIndex];
				if (show) {
					onSelectShow(show);
				}
			} else if (
				index <
				festivalResults.length + showResults.length + artistResults.length
			) {
				const artistIndex = index - festivalResults.length - showResults.length;
				const artist = artistResults[artistIndex];
				if (artist) {
					onSelectArtist(artist);
				}
			} else {
				const userIndex =
					index -
					festivalResults.length -
					showResults.length -
					artistResults.length;
				if (userIndex >= 0 && userIndex < userResults.length) {
					const user = userResults[userIndex];
					if (user?.username) {
						onSelectUser(user.username);
					}
				}
			}
		},
		[
			festivalResults,
			showResults,
			artistResults,
			userResults,
			onSelectFestival,
			onSelectShow,
			onSelectArtist,
			onSelectUser,
		],
	);

	// Keyboard navigation
	const { handleKeyDown } = useSearchKeyboardNav({
		totalResults,
		selectedIndex,
		onSelectedIndexChange,
		onSelect: handleSelectByIndex,
		onClose,
	});

	// Attach keyboard event listener
	useEffect(() => {
		if (isOpen) {
			window.addEventListener("keydown", handleKeyDown);
			return () => window.removeEventListener("keydown", handleKeyDown);
		}
	}, [isOpen, handleKeyDown]);

	if (!isOpen) return null;

	return (
		<div
			className="pointer-events-auto fixed inset-0 z-[9999] flex touch-none bg-background/80 backdrop-blur-sm"
			style={{
				paddingTop: "env(safe-area-inset-top)",
				paddingLeft: "env(safe-area-inset-left)",
				paddingRight: "env(safe-area-inset-right)",
				paddingBottom: "env(safe-area-inset-bottom)",
			}}
			onClick={onClose}
		>
			<div
				ref={searchDialogRef}
				className={cn(
					"flex max-h-full w-full flex-col bg-card",
					"md:m-auto md:max-h-[85vh] md:max-w-md md:rounded-2xl md:shadow-lg md:ring-1 md:ring-border/50",
					"h-full md:h-auto",
					"overflow-hidden",
				)}
				onClick={(e) => e.stopPropagation()}
			>
				<SearchModalInput
					value={searchValue}
					onChange={onSearchValueChange}
					onClose={onClose}
					inputRef={searchInputRef}
					isMobile={isMobile.current}
				/>

				<SearchResults
					searchValue={searchValue}
					userResults={userResults}
					festivalResults={festivalResults}
					showResults={showResults}
					artistResults={artistResults}
					isLoading={isLoading}
					error={error}
					selectedIndex={selectedIndex}
					onSelectUser={onSelectUser}
					onSelectFestival={onSelectFestival}
					onSelectShow={onSelectShow}
					onSelectArtist={onSelectArtist}
					isMobile={isMobile.current}
				/>
			</div>
		</div>
	);
}
